import { type Features, useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { ModalId, useModalStack } from '@/hooks/data/modal/useModalStore';
import dynamic from 'next/dynamic';

const DeleteConfirmationAlertDialog = dynamic(
  () => import('@/components/delete-dialogs/delete-confirmation-dialog').then((m) => m.DeleteConfirmationAlertDialog),
  { ssr: false, loading: () => null },
);

const DeleteNodeAlertComponent = dynamic(
  () => import('@/components/delete-dialogs/delete-node-alert-component').then((m) => m.DeleteNodeAlertComponent),
  { ssr: false, loading: () => null },
);

const AssetUploader = dynamic(() => import('@/components/upload-dialog/asset-uploader').then((m) => m.AssetUploader), {
  ssr: false,
  loading: () => null,
});

const EditCreateFolderAlbumDialog = dynamic(
  () => import('@/components/edit-create-folder-album-dialog').then((m) => m.EditCreateFolderAlbumDialog),
  { ssr: false, loading: () => null },
);

const VersionPreviewDialog = dynamic(
  () => import('@/components/version-preview-dialog').then((m) => m.VersionPreviewDialog),
  { ssr: false, loading: () => null },
);

const RestoreTrashAlertDialog = dynamic(
  () => import('@/components/restore-trash-alert-dialog').then((m) => m.RestoreTrashAlertDialog),
  { ssr: false, loading: () => null },
);

const EditCreateShareDialog = dynamic(
  () => import('@/components/edit-create-share-dialog').then((m) => m.EditCreateShareDialog),
  { ssr: false, loading: () => null },
);

const CreateDestinationDialog = dynamic(
  () => import('@/components/create-dialogs/create-destination-dialog').then((m) => m.CreateDestinationDialog),
  { ssr: false, loading: () => null },
);

const CreateVariantDialog = dynamic(
  () => import('@/components/create-dialogs/create-variant-dialog').then((m) => m.CreateVariantDialog),
  { ssr: false, loading: () => null },
);

const CreateWorkflowDialog = dynamic(
  () => import('@/components/create-dialogs/create-workflow-dialog').then((m) => m.CreateWorkflowDialog),
  { ssr: false, loading: () => null },
);

const CreateWorkflowStatusDialog = dynamic(
  () => import('@/components/create-dialogs/create-workflow-status-dialog').then((m) => m.CreateWorkflowStatusDialog),
  { ssr: false, loading: () => null },
);

const CreateUserDialog = dynamic(
  () => import('@/components/create-dialogs/create-user-dialog').then((m) => m.CreateUserDialog),
  { ssr: false, loading: () => null },
);

const CreateMetadataDialog = dynamic(
  () => import('@/components/create-dialogs/create-metadata-dialog').then((m) => m.CreateMetadataDialog),
  { ssr: false, loading: () => null },
);

const EditCreateSubmissionLinkDialog = dynamic(
  () =>
    import('@/components/submissions/edit-create-submission-link-dialog').then((m) => m.EditCreateSubmissionLinkDialog),
  { ssr: false, loading: () => null },
);

const DownloadZip = dynamic(() => import('@/components/download-zip-dialog').then((m) => m.DownloadZip), {
  ssr: false,
  loading: () => null,
});

const ShareDownload = dynamic(() => import('@/components/download-zip-dialog').then((m) => m.ShareDownload), {
  ssr: false,
  loading: () => null,
});

const ConfirmDialog = dynamic(() => import('@/components/confirm-dialog').then((m) => m.ConfirmDialog), {
  ssr: false,
  loading: () => null,
});

type FeatureFlags = Record<keyof Features, boolean>;
type ModalFeatureFlag = keyof Features | Array<keyof Features>;
type ModalFeatureFlags = Partial<Record<ModalId, ModalFeatureFlag>>;

// Add modal ids here when rendering the modal should be gated by one or more feature flags.
// Examples:
// [ModalId.SomeModal]: ['destinations', 'malware_scanning'] // one modal requiring multiple flags
// [ModalId.FirstModal]: 'destinations', [ModalId.SecondModal]: 'destinations' // multiple modals sharing one flag
const modalFeatureFlags: ModalFeatureFlags = {};

const isModalFeatureEnabled = (modalId: ModalId, features: FeatureFlags) => {
  const featureFlags = modalFeatureFlags[modalId];

  if (!featureFlags) {
    return true;
  }

  return (Array.isArray(featureFlags) ? featureFlags : [featureFlags]).every((feature) => features[feature]);
};

export function ModalHost() {
  const stack = useModalStack();
  const features = useFeatureStore();

  return (
    <>
      {/* Map only currently open stack entries so parent dialogs retain form state behind child dialogs. */}
      {stack.map((entry) => {
        if (!isModalFeatureEnabled(entry.id, features)) {
          return null;
        }

        switch (entry.id) {
          case ModalId.DeleteConfirmation:
            return <DeleteNodeAlertComponent key={entry.key} {...entry.props} />;
          case ModalId.UploadAssetVersion:
            return <AssetUploader key={entry.key} {...entry.props} />;
          case ModalId.CreateAlbum:
          case ModalId.CreateFolder:
          case ModalId.EditFolder:
          case ModalId.EditAlbum:
            return <EditCreateFolderAlbumDialog key={entry.key} modalId={entry.id} id={entry.props?.id} />;
          case ModalId.VersionPreview:
            return <VersionPreviewDialog key={entry.key} {...entry.props} />;
          case ModalId.RestoreTrashConfirmation:
            return <RestoreTrashAlertDialog key={entry.key} />;
          case ModalId.EditCreateShare:
            return <EditCreateShareDialog key={entry.key} {...entry.props} />;
          case ModalId.DeleteConfirmationAlertDialog:
            return <DeleteConfirmationAlertDialog key={entry.key} {...entry.props} />;
          case ModalId.CreateDestination:
            return <CreateDestinationDialog key={entry.key} />;
          case ModalId.CreateVariant:
            return <CreateVariantDialog key={entry.key} variantLimits={entry.props.variantLimits} />;
          case ModalId.CreateWorkflow:
            return <CreateWorkflowDialog key={entry.key} />;
          case ModalId.CreateWorkflowStatus:
          case ModalId.CreateWorkflowStatusInspector:
            return <CreateWorkflowStatusDialog key={entry.key} {...entry.props} modalId={entry.id} />;
          case ModalId.CreateUser:
            return <CreateUserDialog key={entry.key} />;
          case ModalId.CreateMetadata:
            return <CreateMetadataDialog key={entry.key} />;
          case ModalId.EditCreateSubmissionLink:
            return <EditCreateSubmissionLinkDialog key={entry.key} {...entry.props} />;
          case ModalId.DownloadZip:
            return <DownloadZip key={entry.key} {...entry.props} />;
          case ModalId.ShareDownload:
            return <ShareDownload key={entry.key} {...entry.props} />;
          case ModalId.Confirm:
            return <ConfirmDialog key={entry.key} {...entry.props} />;
          default:
            return null;
        }
      })}
    </>
  );
}
